// ORCA360 - Reactive Device Type Hook
// Provides { isMobile, isTablet, isDesktop, isPWA } with live updates on resize/orientation change.
// Breakpoints aligned with Tailwind: mobile < 768px, tablet 768-1024px, desktop > 1024px.

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { isInstalledPWA, MOBILE_MAX_WIDTH, TABLET_MAX_WIDTH } from '../utils/device';

interface DeviceType {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isPWA: boolean;
}

/**
 * Reactive hook that tracks the current device type.
 * Uses window.matchMedia listeners for breakpoint changes and
 * a debounced resize listener as a fallback for orientation changes.
 */
export function useDeviceType(): DeviceType {
  const getDeviceType = useCallback((): DeviceType => {
    if (typeof window === 'undefined') {
      return { isMobile: false, isTablet: false, isDesktop: true, isPWA: false };
    }

    const width = window.innerWidth;
    const isMobile = width <= MOBILE_MAX_WIDTH;
    const isTablet = !isMobile && width <= TABLET_MAX_WIDTH;
    const isDesktop = width > TABLET_MAX_WIDTH;
    const isPWA = isInstalledPWA();

    return { isMobile, isTablet, isDesktop, isPWA };
  }, []);

  const [device, setDevice] = useState<DeviceType>(getDeviceType);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // matchMedia listeners for Tailwind breakpoints
    const mobileQuery = window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`);
    const tabletQuery = window.matchMedia(`(min-width: ${MOBILE_MAX_WIDTH + 1}px) and (max-width: ${TABLET_MAX_WIDTH}px)`);

    const handleChange = () => {
      setDevice(getDeviceType());
    };

    // Modern addEventListener for matchMedia (Safari 14+)
    mobileQuery.addEventListener('change', handleChange);
    tabletQuery.addEventListener('change', handleChange);

    // Debounced resize handler as fallback (covers edge cases like orientation)
    const handleResize = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(handleChange, 200);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleChange);

    return () => {
      mobileQuery.removeEventListener('change', handleChange);
      tabletQuery.removeEventListener('change', handleChange);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleChange);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [getDeviceType]);

  // Memoize the return value to avoid unnecessary re-renders in consumers
  return useMemo(() => device, [device]);
}
